import axios from "axios";
import { api, throwApiError } from "../../api/api";

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type Nr1QuestionType =
  | "BOOLEAN"
  | "SCALE"
  | "TEXT"
  | "DATE"
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | string;

export type Nr1PublicQuestionOption = {
  id: string;
  label: string;
  order: number;
};

export type Nr1PublicQuestion = {
  id: string;
  type: Nr1QuestionType;
  prompt: string;
  required: boolean;
  order: number;
  dimension?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  options: Nr1PublicQuestionOption[];
};

export type Nr1PublicForm = {
  store: { id: string; nomeFantasia: string };
  expiresAt: string;
  form: {
    id: string;
    title: string;
    category: string;
    questions: Nr1PublicQuestion[];
  } | null;
};

export type Nr1Answer = {
  questionId: string;
  booleanValue?: boolean;
  intValue?: number;
  textValue?: string;
  dateValue?: string;
  optionId?: string;
  optionIds?: string[];
};

export type Nr1LinkBatch = {
  id: string;
  store: { id: string; nomeFantasia: string; estado?: string | null };
  form: { id: string; title: string; category?: string | null };
  expiresAt: string;
  revokedAt?: string | null;
  createdAt: string;
  status: "ACTIVE" | "EXPIRED" | "REVOKED";
  totalLinks: number;
  usedLinks: number;
  pendingLinks: number;
};

export type Nr1CreatedBatch = {
  batchId: string;
  expiresAt: string;
  createdAt: string;
  tokens: string[];
};

export type Nr1DimensionScore = {
  dimension: string;
  favorable01: number;
  risk01: number;
  riskLevel: "BAIXO" | "MEDIO" | "ALTO";
  answersCount: number;
};

export type Nr1StoreSummary = {
  storeId: string;
  storeName: string;
  estado?: string | null;
  responses: number;
  overallFavorable01: number;
  overallRisk01: number;
  overallRiskLevel: "BAIXO" | "MEDIO" | "ALTO";
  dimensions: Nr1DimensionScore[];
};

export type Nr1DimensionSummary = {
  dimensions: string[];
  stores: Nr1StoreSummary[];
};

export type Nr1Participation = {
  storeId: string;
  storeName: string;
  estado?: string | null;
  linksGenerated: number;
  linksUsed: number;
  responses: number;
  participationRate: number | null;
};

export type Nr1QuestionBreakdown = {
  questionId: string;
  prompt: string;
  type: Nr1QuestionType;
  order: number;
  dimension: string;
  minValue?: number | null;
  maxValue?: number | null;
  totalAnswers: number;
  favorable01: number | null;
  risk01: number | null;
  distribution: { label: string; count: number }[];
  textAnswers?: string[];
};

export type Nr1EvolutionPoint = {
  month: string;
  responses: number;
  favorable01: number | null;
  risk01: number | null;
  dimensions: { dimension: string; favorable01: number | null; risk01: number | null }[];
};

export type Nr1ReportParams = {
  formId?: string;
  storeId?: string;
  from?: string;
  to?: string;
  months?: number;
};

// ---------------------------------------------------------------------------
// Público (sem login)
// ---------------------------------------------------------------------------

export type Nr1PublicError = {
  kind: "NOT_FOUND" | "GONE" | "OTHER";
  message: string;
};

function parsePublicError(error: unknown, fallback: string): Nr1PublicError {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data as any;
    const message = Array.isArray(data?.message)
      ? data.message.join(" | ")
      : typeof data?.message === "string"
        ? data.message
        : fallback;

    if (status === 404) return { kind: "NOT_FOUND", message };
    if (status === 410) return { kind: "GONE", message };
    return { kind: "OTHER", message };
  }
  return { kind: "OTHER", message: fallback };
}

export const getNr1PublicFormApi = async (token: string): Promise<Nr1PublicForm> => {
  try {
    const { data } = await api.get<Nr1PublicForm>(
      `/public/nr1/${encodeURIComponent(token)}`
    );
    return data;
  } catch (error) {
    throw parsePublicError(error, "Erro ao carregar o formulário.");
  }
};

export const submitNr1ResponseApi = async (
  token: string,
  answers: Nr1Answer[]
): Promise<{ ok: boolean }> => {
  try {
    const { data } = await api.post<{ ok: boolean }>(
      `/public/nr1/${encodeURIComponent(token)}/submit`,
      { answers }
    );
    return data;
  } catch (error) {
    throw parsePublicError(error, "Erro ao enviar as respostas.");
  }
};

// ---------------------------------------------------------------------------
// Admin (JWT)
// ---------------------------------------------------------------------------

export const createNr1LinkBatchApi = async (args: {
  storeId: string;
  formId: string;
  quantity: number;
  expiresAt: string;
}): Promise<Nr1CreatedBatch> => {
  try {
    const { data } = await api.post<Nr1CreatedBatch>(
      `/stores/${encodeURIComponent(args.storeId)}/nr1-links`,
      { formId: args.formId, quantity: args.quantity, expiresAt: args.expiresAt }
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao gerar lote de links.");
    throw error;
  }
};

export const listNr1LinkBatchesApi = async (storeId?: string): Promise<Nr1LinkBatch[]> => {
  try {
    const { data } = await api.get<Nr1LinkBatch[]>("/nr1-link-batches", {
      params: storeId ? { storeId } : {},
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar lotes de links.");
    throw error;
  }
};

export const revokeNr1LinkBatchApi = async (batchId: string) => {
  try {
    const { data } = await api.post(`/nr1-link-batches/${encodeURIComponent(batchId)}/revoke`, {});
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao revogar lote de links.");
    throw error;
  }
};

export const getNr1DimensionSummaryApi = async (
  params: Nr1ReportParams = {}
): Promise<Nr1DimensionSummary> => {
  try {
    const { data } = await api.get<Nr1DimensionSummary>("/nr1/reports/dimension-summary", {
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar o resumo por dimensão.");
    throw error;
  }
};

export const getNr1ParticipationApi = async (
  params: Nr1ReportParams = {}
): Promise<Nr1Participation[]> => {
  try {
    const { data } = await api.get<Nr1Participation[]>("/nr1/reports/participation", { params });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a adesão.");
    throw error;
  }
};

export const getNr1QuestionBreakdownApi = async (
  params: Nr1ReportParams = {}
): Promise<Nr1QuestionBreakdown[]> => {
  try {
    const { data } = await api.get<Nr1QuestionBreakdown[]>("/nr1/reports/question-breakdown", {
      params,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a distribuição por pergunta.");
    throw error;
  }
};

export const getNr1EvolutionApi = async (
  params: Nr1ReportParams = {}
): Promise<Nr1EvolutionPoint[]> => {
  try {
    const { data } = await api.get<Nr1EvolutionPoint[]>("/nr1/reports/evolution", { params });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao carregar a evolução mensal.");
    throw error;
  }
};
